import httpApi from "@/apis/httpApi";
import CryptoJS from "crypto-js";
// 有协议就直接用，没有就补上（默认 https）
export function getNormalizedUrl(urlString) {
    const normalized =
        urlString && /^https?:\/\//i.test(urlString)
            ? urlString
            : urlString && urlString.startsWith("//")
            ? "https:" + urlString
            : "https://" + urlString;
    return new URL(normalized);
}

// 检测是否为文件上传请求
export function isFileUploadRequest(config) {
    if (!config.data) return false;
    // 检查是否为 FormData
    if (config.data instanceof FormData) return true;
    // 检查Content-Type
    const contentType =
        config.headers &&
        (config.headers["Content-Type"] || config.headers["content-type"]);
    if (contentType && contentType.includes("multipart/form-data")) return true;
    return false;
}

// 提取文件上传metaData
export function extractMetaData(data) {
    if (!data) return "";
    // 如果FormData，尝试获取meta 字段
    if (data instanceof FormData) {
        const meta = data.get("meta") || data.get("metaData");
        if (meta) {
            return typeof meta === "string" ? meta : JSON.stringify(meta);
        }
    }
    // 如果是普通对象，尝试获取 meta metaData 字段
    if (typeof data === "object" && data !== null) {
        const meta = data.meta || data.metaData;
        if (meta) {
            return typeof meta === "string" ? meta : JSON.stringify(meta);
        }
    }
    return "";
}

// 构造请求绝对地址（包含查询参数）
export function buildRequestPath(url, params, method) {
    let path = url.pathname;

    // GET请求需要将params编码后拼接到pathname
    if (method && method.toUpperCase() === "GET" && params) {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
            if (params[key] !== undefined && params[key] !== null) {
                queryParams.append(key, String(params[key]));
            }
        });
        const queryString = queryParams.toString();
        if (queryString) {
            path += "?" + queryString;
        }
    }

    return path;
}

// 处理请求体，返回字符串形式
export function getRequestBody(data, isFileUpload) {
    if (isFileUpload) {
        // 文件上传使用 metaData
        const metaData = extractMetaData(data);
        if (metaData) {
            // 限制长度300000字符以内
            return metaData.length > 300000
                ? metaData.substring(0, 300000)
                : metaData;
        }
        return "";
    }

    // POST请求转为JSON字符串
    if (data === null || data === undefined) {
        return "";
    }

    // 如果已经是字符串，检查是否是双重序列化的 JSON 字符串
    if (typeof data === "string") {
        // 如果字符串看起来是被 JSON.stringify 处理过的（包含转义引号），尝试解析一次
        if (data.startsWith('"') && data.includes('\\"')) {
            try {
                const parsed = JSON.parse(data);
                // 如果解析后还是字符串，说明确实是双重序列化，返回解析后的字符串
                if (typeof parsed === "string") {
                    return parsed.length > 300000
                        ? parsed.substring(0, 300000)
                        : parsed;
                }
                // 如果解析后是对象，重新序列化为字符串
                const jsonStr = JSON.stringify(parsed);
                return jsonStr.length > 300000
                    ? jsonStr.substring(0, 300000)
                    : jsonStr;
            } catch (e) {
                // 解析失败，说明不是有效的 JSON，直接返回原字符串
            }
        }

        return data.length > 300000 ? data.substring(0, 300000) : data;
    }

    // 转为JSON字符串
    const jsonStr = JSON.stringify(data);
    // const jsonStr = data;
    // 限制长度300000字符以内
    return jsonStr.length > 300000 ? jsonStr.substring(0, 300000) : jsonStr;
}

// 统一编码 GET 查询参数，避免中文等字符导致签名不一致
function encodeUrlQueryForSign(rawUrl = "") {
    if (!rawUrl || typeof rawUrl !== "string") return rawUrl;
    const [path, query = ""] = rawUrl.split("?");
    if (!query) return rawUrl;

    const encodedQuery = query
        .split("&")
        .filter((pair) => pair !== "")
        .map((pair) => {
            const [rawKey, ...rest] = pair.split("=");
            const rawValue = rest.join("=");
            const safeDecode = (text) => {
                try {
                    return decodeURIComponent(text || "");
                } catch (e) {
                    return text || "";
                }
            };
            const key = encodeURIComponent(safeDecode(rawKey));
            const value = encodeURIComponent(safeDecode(rawValue));
            return `${key}=${value}`;
        })
        .join("&");

    return encodedQuery ? `${path}?${encodedQuery}` : path;
}

// hmacSha256
async function hmacSha256(message, secret) {
    // console.log("加密数据:", message, 11111);
    // console.log("秘钥：", secret, 22222);
    return CryptoJS.HmacSHA256(message, secret);
}

// E2SignCer
async function E2SignCer({ data, param, signObj }) {
    // 生成随机数
    const chars = [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
    ];
    let resNum = "";
    for (let i = 0; i < 32; i++) {
        const id = Math.ceil(Math.random() * 35);
        resNum += chars[id];
    }
    const rqtime = Date.parse(String(new Date()));
    let auth = `BOXAPP-SHA256 NONCE=${resNum};TIMESTAMP=${rqtime};`; //BOXAPP-SHA256
    if (signObj) {
        let url = signObj.url;

        if (
            param &&
            typeof param === "object" &&
            !Array.isArray(param) &&
            Object.keys(param).length > 0
        ) {
            const paramstr = Object.entries(param)
                .map(
                    ([key, value]) =>
                        `${encodeURIComponent(String(key))}=${encodeURIComponent(
                            String(value),
                        )}`,
                )
                .join("&");
            if (paramstr !== "") {
                url = url.includes("?")
                    ? `${url}&${paramstr}`
                    : `${url}?${paramstr}`;
            }
        }
        const signUrl =
            signObj.method === "GET" ? encodeUrlQueryForSign(url) : url;
        let value = `${signObj.method}\n${signUrl}\n${rqtime}\n${resNum}\n${
            signObj.deviceType + "-h5"
        }\n${signObj.deviceId ? signObj.deviceId : ""}\n${signObj.appv}\n${
            signObj.token ? signObj.token : ""
        }\n${navigator.userAgent}\n`;
        if (data) {
            if (Object.keys(data).length === 0) {
                //{}空对象
                value += `\n`;
            } else {
                value += `${data}\n`;
            }
        } else {
            //''空字符串
            value += `\n`;
        }
        const signature = await hmacSha256(value, httpApi.signKeyV3);
        auth += `SIGNATURE=${signature}`;
    } else {
        const signature = await hmacSha256("", httpApi.signKeyV3);
        auth += `SIGNATURE=${signature}`;
    }
    return auth;
}

export async function toSignV3(requestBody, signObj) {
    // 接口验签
    signObj.method = String(signObj.method || "").toUpperCase();
    // 这里保留对象处理逻辑仅作为兼容性保护
    let requestBodyStr = "";
    if (typeof requestBody === "string") {
        requestBodyStr = requestBody;
    } else if (requestBody !== undefined && requestBody !== null) {
        // 兼容性处理：如果直接传入对象，转为 JSON 字符串
        // 注意：正常情况下应该由 getRequestBody 处理，这里不应该执行
        try {
            requestBodyStr = JSON.stringify(requestBody);
        } catch (e) {
            requestBodyStr = "";
        }
    }
    const isGetMethod = signObj.method === "GET";
    const data = isGetMethod ? {} : requestBodyStr || {};
    const param = "";
    const sign = await E2SignCer({
        data,
        param,
        signObj,
    });
    // console.log("新生成的：X-BOX-AUTHORIZATION：", sign, 333);
    return sign;
}
